import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, MessageCircle, ArrowRight } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { formatINR } from "@/lib/utils-format";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import WhatsAppCTA from "@/components/WhatsAppCTA";

export default function Cart() {
    const { cart, removeItem, updateQty, subtotal, count } = useCart();
    const [settings, setSettings] = useState({ whatsapp_number: "919971925446" });
    useEffect(() => {
        document.title = "Cart | signiastylettoix.com";
        api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    if (count === 0) {
        return (
            <section className="section">
                <div className="container-x max-w-[600px] text-center">
                    <h1 className="font-serif text-[40px] md:text-[52px]">Your cart is empty.</h1>
                    <p className="text-[16px] text-[#4a5568] mt-4">Browse the Styletto IX range and add a model to get started.</p>
                    <Link to="/products" className="btn-primary mt-7 inline-flex" data-testid="empty-cart-shop">
                        Shop Styletto IX <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        );
    }

    return (
        <section className="section">
            <div className="container-x grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-8">
                    <h1 className="font-serif text-[40px] md:text-[52px] leading-tight">Your Cart</h1>
                    <p className="eyebrow mt-3">{count} item{count > 1 ? "s" : ""}</p>
                    <div className="mt-8 space-y-4">
                        {cart.items.map((it, i) => (
                            <div key={i} className="bg-white rounded-2xl border border-black/5 p-5 flex gap-4 items-center" data-testid={`cart-item-${i}`}>
                                <img src={it.image} alt={it.product_name} className="w-24 h-24 object-contain bg-[#f3f4f6] rounded-xl" />
                                <div className="flex-1">
                                    <h3 className="font-serif text-[20px] text-[#1a1a1a]">{it.product_name}</h3>
                                    <p className="text-[12.5px] text-[#4a5568] mt-1">
                                        {it.side ? `Side: ${it.side}` : ""}{it.color ? ` · Color: ${it.color}` : ""}
                                    </p>
                                    <p className="text-[15px] font-semibold text-[#1a1a1a] mt-2">{formatINR(it.price)}</p>
                                </div>
                                <div className="flex flex-col items-end gap-3">
                                    <div className="flex items-center gap-1 border border-[#E5E7EB] rounded-full px-1">
                                        <button onClick={() => updateQty(i, it.qty - 1)} className="w-8 h-8 flex items-center justify-center" data-testid={`cart-dec-${i}`}><Minus size={14} /></button>
                                        <span className="w-7 text-center text-[14px]">{it.qty}</span>
                                        <button onClick={() => updateQty(i, it.qty + 1)} className="w-8 h-8 flex items-center justify-center" data-testid={`cart-inc-${i}`}><Plus size={14} /></button>
                                    </div>
                                    <button onClick={() => removeItem(i)} className="text-[12.5px] text-[#9CA3AF] hover:text-red-500 inline-flex items-center gap-1" data-testid={`cart-remove-${i}`}>
                                        <Trash2 size={12} /> Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <aside className="lg:col-span-4">
                    <div className="bg-[#f8f9fa] rounded-3xl p-7 lg:sticky lg:top-24">
                        <h3 className="font-serif text-[24px] text-[#1a1a1a]">Order summary</h3>
                        <div className="mt-5 space-y-2 text-[14.5px]">
                            <Row label="Subtotal" value={formatINR(subtotal)} />
                            <Row label="Delivery" value="Free" />
                        </div>
                        <div className="mt-5 pt-5 border-t border-[#E5E7EB] flex items-end justify-between">
                            <span className="text-[14px] text-[#4a5568]">Estimated total</span>
                            <span className="font-serif text-[28px] text-[#1a1a1a]">{formatINR(subtotal)}</span>
                        </div>
                        <Link to="/checkout" className="btn-primary w-full justify-center mt-6" data-testid="cart-checkout-btn">
                            Proceed to Checkout <ArrowRight size={16} />
                        </Link>
                        <WhatsAppCTA
                            waNumber={settings.whatsapp_number}
                            message="Hi, I have items in my cart on signiastylettoix.com — please share the best price."
                            sourcePage="cart"
                            className="btn-whatsapp w-full justify-center mt-3"
                            data-testid="cart-whatsapp-btn"
                        >
                            <MessageCircle size={16} /> Get Best Price on WhatsApp
                        </WhatsAppCTA>
                        <p className="text-[12px] text-[#9CA3AF] mt-4 leading-relaxed">
                            A HearClear audiologist will contact you to confirm fitting and audiogram before delivery.
                        </p>
                    </div>
                </aside>
            </div>
        </section>
    );
}

const Row = ({ label, value }) => (
    <div className="flex items-center justify-between"><span className="text-[#4a5568]">{label}</span><span className="text-[#1a1a1a] font-medium">{value}</span></div>
);
